import { Paperclip, RefreshCw, Upload } from 'lucide-react';

import { usePermission } from '../../../../auth/AuthContext';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { Tooltip } from '../../../../components/Tooltip';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import type { AwardNomineeDetail, AwardNomineeDocument } from '../praiseTypes';

/**
 * A file's action glyph, at the 32px floor a table's action gutter takes — the 201 file Documents
 * panel's own control, for its reason: a 40px button is taller than the line it acts on.
 */
const GLYPH_BUTTON =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-400 ' +
  'hover:bg-slate-100 hover:text-slate-700 disabled:text-slate-300 disabled:hover:bg-transparent';

/**
 * The nominee's checklist, one row per document — **the 201 file's Documents panel is the standard**
 * (`personnel/components/DocumentsPanel.tsx`) and this copies its value cell: the description with
 * Upload at the OUTER RIGHT, then `Not submitted` or the file's line — `Paperclip`, the name OPENING
 * the preview (where the submitted-by and -at facts live), Replace revealed on the line's hover.
 *
 * **ONE file per line**, so Upload is offered until the document is submitted and Replace moves onto
 * the file's own line — two glyphs for one act would be the same control twice. There is no Delete:
 * a submitted line is replaced, never emptied.
 *
 * Drawn only where `onUpload` is passed (Nomination) AND the reader holds `award-nominations.update`;
 * Evaluation passes none and reads. **No Edit on this card** — its state moves by an action, not typing.
 */
export function NomineeDocumentsCard({
  id,
  record,
  busy,
  onUpload,
  onOpen,
}: {
  id: string;
  record: AwardNomineeDetail;
  busy: boolean;
  /** Absent on Evaluation — an evaluator reads the papers and replaces none. */
  onUpload?: (document: AwardNomineeDocument) => void;
  onOpen: (document: AwardNomineeDocument) => void;
}) {
  const canUpload = usePermission('award-nominations.update') && onUpload != null;

  // Disabling is for STATE, and the tooltip carries the reason in place of the verb.
  const closedReason = !record.awardIsActive
    ? 'This award is deleted. Restore it before uploading.'
    : !record.isActive
      ? 'This nomination is deleted. Restore it before uploading.'
      : null;

  const rows: PreviewRow[] = record.documents.map((document) => {
    const submitted = document.fileName != null;
    const offersUpload = canUpload && !submitted;

    return {
      label: document.name,
      value: (
        <div className="flex min-w-0 flex-col gap-1.5">
          {/* The line drops when there is neither a description nor an Upload, rather than leaving a gap. */}
          {document.description || offersUpload ? (
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span className="min-w-0 text-xs text-slate-400">{document.description}</span>

              {offersUpload ? (
                <Tooltip label={closedReason ?? 'Upload'}>
                  <button
                    type="button"
                    id={`award-nominee-document-upload-${document.id}`}
                    onClick={() => onUpload?.(document)}
                    disabled={busy || closedReason != null}
                    aria-label={`Upload ${document.name}`}
                    className={GLYPH_BUTTON}
                  >
                    <Upload className="h-4 w-4" aria-hidden="true" />
                  </button>
                </Tooltip>
              ) : null}
            </div>
          ) : null}

          {submitted ? (
            <span className="group/line flex min-w-0 items-center gap-2">
              <Paperclip className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />

              {/* The WRAPPER takes `flex-1`, so the blank run after the name is not a click target. */}
              <span className="flex min-w-0 flex-1 items-center">
                <button
                  type="button"
                  onClick={() => onOpen(document)}
                  disabled={busy}
                  className="min-w-0 truncate text-left font-medium text-slate-800 hover:underline disabled:text-slate-400"
                >
                  {document.fileName}
                </button>
              </span>

              {/* Revealed on the LINE's hover and on focus — `opacity-0`, never `hidden`; painted where there is no hover. */}
              {canUpload ? (
                <span className="flex shrink-0 items-center opacity-0 focus-within:opacity-100 group-hover/line:opacity-100 [@media(hover:none)]:opacity-100">
                  <Tooltip label={closedReason ?? 'Replace'}>
                    <button
                      type="button"
                      id={`award-nominee-document-upload-${document.id}`}
                      onClick={() => onUpload?.(document)}
                      disabled={busy || closedReason != null}
                      aria-label={`Replace ${document.fileName}`}
                      className={GLYPH_BUTTON}
                    >
                      <RefreshCw className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </Tooltip>
                </span>
              ) : null}
            </span>
          ) : (
            /* Not an em dash: the nominee owes this document, which is the fact the reader scans for. */
            <span className="text-slate-400">Not submitted</span>
          )}
        </div>
      ),
    };
  });

  return (
    <ProfileCard id={id} title="Document requirements" count={record.documents.length}>
      <PreviewTable variant="table-display" rows={rows} />
    </ProfileCard>
  );
}
